// Full landing-page translation set. Numbers/percentages/proper nouns (EXOPILOT,
// SOP, BLE) are kept as-is across languages -- standard practice for a technical
// product name and industry-standard abbreviations. Arabic renders right-to-left
// (see LanguageContext, which sets dir="rtl" on <html> for "ar").

const en = {
  nav: { how: "How it works", platform: "Platform", privacy: "Privacy", trust: "Trust Center", contact: "Contact", signIn: "Sign in", getStarted: "Get started", bookDemo: "Book a demo" },
  hero: {
    eyebrow: "Service quality intelligence",
    titleLine: "Measure how your team performs.",
    titleAccent: "Audio never leaves your premises.",
    subtitle: "EXOPILOT scores your service teams against your own SOPs — on-premise processing, cloud visibility.",
    ctaConsole: "Open your console",
    ctaBookDemo: "Book your demo",
    ctaArchitecture: "See how it works",
    ctaWatchFilm: "Watch how EXOPILOT works",
    liveFloorView: "Live floor view",
    loopPremises: "Your premises",
    loopConsole: "Console score",
    loopCaption: "Only the score leaves your building — audio never does.",
    loopStages: [
      "Pendant records the conversation",
      "Sent to your local server over BLE",
      "Scored against your SOP — 94.2 reaches your console",
    ],
    loopPendantLabel: "Pendant",
    loopBleLabel: "BLE",
    loopServerLabel: "Local server",
    loopSuggestionLabel: "AI suggestion",
    loopSuggestionText: "\u201cI'd be happy to check that for you.\u201d",
    deviceBadge: "Recording locally, right now",
  },
  trustChips: ["Voice never leaves your premises", "Score-only cloud uplink", "Encrypted SOP sync", "Strict tenant isolation"],
  industriesLabel: "Built for floors where service is the product",
  industries: ["Luxury hotels", "Jewellery retail", "Private banking", "Fine dining", "Flagship stores", "Concierge desks"],
  heroStats: [["94.2", "Average service score"], ["0", "Audio files in the cloud"], ["100%", "SOP coverage"]],
  how: {
    eyebrow: "How it works",
    heading: "A closed loop between your floor, your local server and your console.",
    steps: [
      { n: "01", title: "Pendant records every interaction", text: "Each employee wears a discreet EXOPILOT pendant that records customer interactions and streams them over BLE to its paired companion phone app." },
      { n: "02", title: "Phone app relays to your local server", text: "The app forwards recordings to your on-premise server, where an offline AI engine scores every interaction against your SOP — no audio ever leaves the building." },
      { n: "03", title: "Only scores reach the cloud", text: "Numeric quality scores sync upward to your cloud dashboard. SOPs sync downward. Voice data stays local, always." },
    ],
    videoTitle: "Watch how EXOPILOT works",
    videoCaption: "Product film · 2 min",
    videoBadge: "From pendant to score — the full loop",
    videoToast: "The full EXOPILOT product film is on its way",
    videoToastDesc: "Meanwhile, open your console and explore the live demo data.",
  },
  platform: {
    eyebrow: "Platform",
    heading: "Everything your quality team needs, nothing your compliance team fears.",
    features: [
      { title: "SOP-driven evaluation", text: "Upload one standard operating procedure per department. Every interaction is measured against it — greeting, adherence, tone, resolution." },
      { title: "Device assignment by serial", text: "Assign pendants to staff with a serial number and app ID. The mapping syncs to your local server automatically." },
      { title: "Privacy by architecture", text: "Voice never transits the cloud. The uplink carries scores only; the downlink carries SOPs only. Auditable by design." },
      { title: "Per-employee progress", text: "Trend lines, compliance breakdowns and session histories for every team member, updated as the local server reports in." },
    ],
  },
  aiCoaching: {
    eyebrow: "Beyond the score",
    heading: "AI coaching recommendations, not just a number.",
    intro: "Every dashboard session includes a plain-language coaching note generated from the same SOP the score was measured against — what went well, what to work on next time, and why. Managers get a starting point for the conversation, not just a grade.",
    bullets: [
      "Generated per session, grounded in the exact SOP step that was missed or done well — never generic advice.",
      "Surfaces the pattern across a week, not just one call — repeated gaps become a coaching priority automatically.",
      "Custom training modules can be built around your own recurring gaps — ask us about tailoring a training track to your floor.",
    ],
    cardLabel: "AI coaching note",
    notes: [
      { name: "Ram · Security", body: "Greeting was warm and on-time. Resolution stalled at the verification step — walk through the ID-check script once more this week before moving to close." },
      { name: "Priya · Front Desk", body: "Strong SOP adherence throughout. Tone dipped when the guest raised a billing dispute — a short de-escalation refresher would help before the next peak weekend." },
      { name: "Arjun · Concierge", body: "Resolution and closing were excellent — request confirmed back to the guest clearly. Greeting was rushed in the first ten seconds; slow down before jumping into the ask." },
    ],
  },
  replay: {
    eyebrow: "See the evidence",
    heading: "Replay the conversation, not just the score.",
    intro: "Every session score breaks down step by step against the transcript that produced it — so a coaching conversation starts from evidence, never a guess.",
    playerLabel: "Sample session · Front Desk",
    steps: [
      { time: "00:04", label: "Greeting", note: "Warm opening, guest name used within 8 seconds.", score: 96 },
      { time: "00:41", label: "SOP adherence", note: "Verification step skipped — passport not requested before room assignment.", score: 58 },
      { time: "02:15", label: "Tone & courtesy", note: "Patient and polite throughout, even after the guest raised his voice.", score: 91 },
      { time: "03:02", label: "Resolution", note: "Request resolved and confirmed back to the guest clearly.", score: 88 },
    ],
  },
  privacySection: {
    eyebrow: "Privacy model",
    heading: "Cloud sees numbers.",
    headingAccent: "Only your building hears voices.",
    bullets: [
      "SOP documents sync from console to local server over an authenticated, key-protected channel.",
      "The local evaluation engine runs fully offline against your SOPs — no internet required to score.",
      "Only structured score payloads travel back up. No transcripts, no recordings, no exceptions.",
      "Every workspace is tenant-isolated; brute-force protection and hashed credentials guard the console.",
    ],
    readFull: "Read the full privacy policy",
    diagramHint: "Click a component to see what it sends and receives",
    arrows: { sops: "SOPs", scores: "Scores", recordings: "Recordings" },
    premisesBoundary: "Premises boundary",
    voiceNeverCrosses: "Voice data never crosses this line",
    nodes: {
      cloud: { label: "EXOPILOT Cloud Console", detail: "Scores · SOPs · device registry", sends: "SOP documents, employee–device mappings (down to your local server)", receives: "Numeric quality scores only, plus device battery/last-seen telemetry" },
      server: { label: "Your Local Server", detail: "Offline AI scoring against your SOPs", sends: "Quality scores, session metadata, device telemetry (up to the cloud)", receives: "Recordings from the pendant/phone app; SOPs from the cloud" },
      device: { label: "Pendant + Phone App", detail: "BLE pairing on the service floor", sends: "Raw audio, over Bluetooth Low Energy, to the paired phone app only", receives: "Nothing from the cloud — it never talks to the internet directly" },
    },
  },
  trustCenterTeaser: {
    eyebrow: "For security & procurement teams",
    heading: "Security & Trust Center",
    text: "Encryption, tenant isolation, access control, audit logging and our vulnerability reporting process — everything an enterprise review asks for, in one place.",
    cta: "Visit Trust Center",
  },
  trustStats: [["0", "voice files ever stored in the cloud"], ["100%", "of scoring runs on your premises"], ["4", "SOP compliance dimensions measured"], ["<1 min", "from device assignment to local sync"]],
  whereWeOperate: {
    eyebrow: "Where we operate",
    heading: "Trusted across India's service floors.",
    text: "From heritage hotels to private banking floors — hover a marker to see the kind of property running EXOPILOT there.",
    sampleNote: "sample locations shown · illustrative",
  },
  dimensions: {
    eyebrow: "What gets measured",
    heading: "Four dimensions.",
    headingAccent: "One published standard.",
    intro: "Every session is scored against the same four dimensions, derived from your department's SOP. Employees see the same criteria their coaches see — nothing is hidden, nothing is arbitrary.",
    items: [
      { name: "Greeting & welcome", score: 92, desc: "Did the interaction open to your standard — salutation, name usage, warmth in the first thirty seconds." },
      { name: "SOP adherence", score: 87, desc: "Step-by-step compliance with the department procedure, from verification through to hand-off." },
      { name: "Tone & courtesy", score: 90, desc: "Politeness, patience and pace held across the full conversation — not just the first minute." },
      { name: "Resolution & closing", score: 84, desc: "Was the customer's need actually met, and was the interaction closed to your closing standard." },
    ],
  },
  hotels: {
    eyebrow: "Use case · Hospitality",
    heading: "Built for hotels where every guest interaction is the brand.",
    intro: "From arrival to check-out, EXOPILOT gives hotel operators the same evidence-based coaching loop across front desk, concierge, housekeeping and F&B — without a single recording ever leaving the property.",
    cards: [
      { title: "Front desk & arrivals", text: "Every check-in scored against your welcome standard — salutation, upsell offer, room-orientation script — so a guest's first ninety seconds are consistent across every shift." },
      { title: "Concierge & guest requests", text: "Resolution time and follow-through tracked against your SOP, so a promised reservation or amenity request never quietly falls through." },
      { title: "F&B & in-room dining", text: "Order accuracy, allergen-check adherence and tone measured the same way across every outlet on the property, not just the flagship restaurant." },
    ],
    valueLabel: "The business case",
    values: [
      { stat: "+9 pts", text: "Typical front-desk score improvement in one quarter once coaching moves from anecdote to evidence." },
      { stat: "0", text: "Guest conversations that ever leave the property — nothing for legal or a privacy-conscious guest to worry about." },
      { stat: "1 SOP", text: "Per department, versioned — every new hire is measured against the exact standard your best performers already meet." },
    ],
    cta: "Talk to us about your property",
  },
  rollout: {
    eyebrow: "Deployment",
    heading: "From empty console to live scoring in two days.",
    items: [
      { when: "Day 1 · Morning", what: "Create your console, configure departments and upload your SOP documents." },
      { when: "Day 1 · Afternoon", what: "Stand up the local server on-site and pair pendants through the companion phone app." },
      { when: "Day 2", what: "Assign devices to employees by serial number — mappings and SOPs sync down automatically." },
      { when: "Day 3 onwards", what: "Scores stream in after every shift. Coaching conversations start with evidence, not anecdotes." },
    ],
  },
  testimonials: {
    eyebrow: "What operators say",
    heading: "Trusted where a single conversation carries the brand.",
    items: [
      { quote: "We finally coach with evidence instead of anecdotes. Front-desk scores rose nine points in one quarter — and our guests' conversations never left the property.", name: "Rukmini Varma", role: "General Manager · heritage palace hotel, Kochi" },
      { quote: "In fine jewellery, one poorly handled consultation can cost a three-generation relationship. EXOPILOT shows us exactly where conversations drift from our standard.", name: "Arjun Mehta", role: "Retail Director · fine jewellery chain, Tamil Nadu" },
      { quote: "Legal signed off in a week. There is nothing to review in the cloud except numbers — that simplicity is the entire audit.", name: "Claire Dubois", role: "Head of Compliance · private banking group" },
    ],
  },
  faq: {
    eyebrow: "Questions",
    heading: "Asked by every operations and compliance team.",
    introPrefix: "Something more specific? ",
    writeToUs: "Write to us",
    orRead: " or read the ",
    fullPolicy: "full privacy policy",
    items: [
      { q: "Does any audio ever reach the cloud?", a: "No. The cloud exposes no endpoint capable of receiving audio. Recordings travel from the pendant over BLE to the companion phone app and terminate at your on-premise server. The uplink is validated to structured numeric scores only — there is no configuration in which voice data can be uploaded." },
      { q: "What do employees actually wear?", a: "A lightweight pendant recorder, available as either a clip-on or chain model, designed to be worn throughout the shift." },
      { q: "What happens if our internet goes down?", a: "Scoring continues uninterrupted — the evaluation engine runs fully offline on your local server against the last synced SOPs. Scores queue locally and sync to your console once connectivity returns. Nothing on the service floor depends on the cloud." },
      { q: "How do we keep this fair and lawful for staff?", a: "EXOPILOT is built for criteria-based coaching, not covert surveillance. We require deploying organisations to inform employees, obtain any consent local law requires, and share the SOPs they are measured against. Every score traces back to a published, versioned standard." },
      { q: "Can we update SOPs after rollout?", a: "Yes. Upload a new version to the SOP library at any time; it syncs to your local server on the next cycle and subsequent sessions are scored against it. Each department carries one active, versioned SOP so there is never ambiguity about the standard in force." },
      { q: "How long does deployment take?", a: "Most single-site deployments are scoring live interactions within two days: console setup and SOP upload on day one, local server and pendant pairing the same day, device assignment on day two. Multi-site rollouts repeat the same pattern per property." },
    ],
  },
  finalCta: { heading: "Bring measurable standards to your service floor.", text: "Set up your console, add your team and sync your first SOP in minutes.", ctaGetStarted: "Get started", ctaBookDemo: "Book a demo" },
  contact: {
    eyebrow: "Get in touch",
    heading: "Talk to us about your floor.",
    text: "Tell us about your properties, showrooms or branches and we'll help you scope a rollout.",
    nameLabel: "Name", namePlaceholder: "Jane Doe",
    emailLabel: "Work email", emailPlaceholder: "you@company.com",
    messageLabel: "Message", messagePlaceholder: "Tell us about your floor, sites and timeline.",
    send: "Send message", sending: "Sending…",
    toastTitle: "Message sent", toastDesc: "We'll get back to you within one business day.",
  },
  bookDemo: {
    title: "Book your demo",
    desc: "30 minutes with our team — see the console, the local server, and the pendant in action.",
    nameLabel: "Name", companyLabel: "Company", emailLabel: "Work email", phoneLabel: "Phone (optional)",
    submit: "Request a slot", submitting: "Sending…",
    toastTitle: "Demo requested", toastDesc: "We'll follow up within one business day.",
  },
  stickyCta: { label: "Book a demo" },
  chatbot: {
    launcherLabel: "Chat with us",
    title: "EXOPILOT Assistant",
    subtitle: "Ask about the product, privacy or pricing",
    placeholder: "Ask a question…",
    send: "Send",
    greeting: "Hi! I can answer questions about EXOPILOT — how the pendant works, what stays on-premise, pricing, deployment. What would you like to know?",
    notConfigured: "The AI assistant isn't connected yet on this deployment — please use the contact form below or write to hello@exopilot.io and a person will get back to you.",
    error: "Something went wrong reaching the assistant — please try again, or use the contact form below.",
    offTopic: "I can only help with questions about EXOPILOT itself — the product, privacy model, deployment, or pricing. For anything else, please use the contact form.",
  },
  footer: { howItWorks: "How it works", platform: "Platform", contact: "Contact", trust: "Security & Trust", privacy: "Privacy Policy", cookies: "Cookies Policy", terms: "Terms of Service", tagline: "© 2026 EXOPILOT · Service quality intelligence" },
};

const hi = {
  nav: { how: "यह कैसे काम करता है", platform: "प्लेटफ़ॉर्म", privacy: "गोपनीयता", trust: "ट्रस्ट सेंटर", contact: "संपर्क करें", signIn: "साइन इन करें", getStarted: "शुरू करें", bookDemo: "डेमो बुक करें" },
  hero: {
    eyebrow: "सेवा गुणवत्ता इंटेलिजेंस",
    titleLine: "अपनी टीम का प्रदर्शन मापें।",
    titleAccent: "ऑडियो कभी आपके परिसर से बाहर नहीं जाता।",
    subtitle: "EXOPILOT आपकी सेवा टीमों को आपकी SOP के अनुसार स्कोर करता है — ऑन-प्रिमाइस प्रोसेसिंग, क्लाउड विज़िबिलिटी।",
    ctaConsole: "अपना कंसोल खोलें",
    ctaArchitecture: "आर्किटेक्चर देखें",
    ctaBookDemo: "अपना डेमो बुक करें",
    ctaWatchFilm: "देखें EXOPILOT कैसे काम करता है",
    liveFloorView: "लाइव फ़्लोर व्यू",
    loopPremises: "आपका परिसर",
    loopConsole: "कंसोल स्कोर",
    loopCaption: "केवल स्कोर आपकी इमारत से बाहर जाता है — ऑडियो कभी नहीं।",
    loopStages: [
      "पेंडेंट बातचीत रिकॉर्ड करता है",
      "BLE के ज़रिए आपके लोकल सर्वर पर भेजा जाता है",
      "आपके SOP के अनुसार स्कोर किया जाता है — 94.2 आपके कंसोल तक पहुँचता है",
    ],
    loopPendantLabel: "पेंडेंट",
    loopBleLabel: "BLE",
    loopServerLabel: "लोकल सर्वर",
    loopSuggestionLabel: "AI सुझाव",
    loopSuggestionText: "\u201cज़रूर, मैं आपके लिए अभी जांच करता/करती हूँ।\u201d",
    deviceBadge: "अभी, स्थानीय रूप से रिकॉर्ड हो रहा है",
  },
  trustChips: ["आवाज़ कभी आपके परिसर से बाहर नहीं जाती", "केवल स्कोर क्लाउड अपलिंक", "एन्क्रिप्टेड SOP सिंक", "सख्त टेनेंट आइसोलेशन"],
  industriesLabel: "उन फ़्लोर्स के लिए बनाया गया जहाँ सेवा ही उत्पाद है",
  industries: ["लक्ज़री होटल", "ज्वेलरी रिटेल", "प्राइवेट बैंकिंग", "फाइन डाइनिंग", "फ्लैगशिप स्टोर", "कॉन्सियर्ज डेस्क"],
  heroStats: [["94.2", "औसत सेवा स्कोर"], ["0", "क्लाउड में ऑडियो फ़ाइलें"], ["100%", "SOP कवरेज"]],
  how: {
    eyebrow: "यह कैसे काम करता है",
    heading: "आपके फ़्लोर, आपके लोकल सर्वर और आपके कंसोल के बीच एक बंद लूप।",
    steps: [
      { n: "01", title: "पेंडेंट हर बातचीत रिकॉर्ड करता है", text: "हर कर्मचारी एक सुव्यवस्थित EXOPILOT पेंडेंट पहनता है जो ग्राहक बातचीत रिकॉर्ड करता है और उसे BLE के ज़रिए जोड़े गए फ़ोन ऐप पर स्ट्रीम करता है।" },
      { n: "02", title: "फ़ोन ऐप आपके लोकल सर्वर तक पहुंचाता है", text: "ऐप रिकॉर्डिंग को आपके ऑन-प्रिमाइस सर्वर तक भेजता है, जहाँ एक ऑफ़लाइन AI इंजन हर बातचीत को आपकी SOP के अनुसार स्कोर करता है — कोई भी ऑडियो कभी बिल्डिंग से बाहर नहीं जाता।" },
      { n: "03", title: "केवल स्कोर क्लाउड तक पहुंचते हैं", text: "संख्यात्मक गुणवत्ता स्कोर आपके क्लाउड डैशबोर्ड तक सिंक होते हैं। SOP नीचे की ओर सिंक होते हैं। आवाज़ का डेटा हमेशा लोकल ही रहता है।" },
    ],
    videoTitle: "देखें EXOPILOT कैसे काम करता है",
    videoCaption: "प्रोडक्ट फ़िल्म · 2 मिनट",
    videoBadge: "पेंडेंट से स्कोर तक — पूरा लूप",
    videoToast: "पूरी EXOPILOT प्रोडक्ट फ़िल्म जल्द आ रही है",
    videoToastDesc: "तब तक, अपना कंसोल खोलें और लाइव डेमो डेटा देखें।",
  },
  platform: {
    eyebrow: "प्लेटफ़ॉर्म",
    heading: "जो आपकी क्वालिटी टीम को चाहिए, वह सब — जिससे आपकी कम्प्लायंस टीम को कोई डर नहीं।",
    features: [
      { title: "SOP-आधारित मूल्यांकन", text: "प्रति विभाग एक मानक संचालन प्रक्रिया (SOP) अपलोड करें। हर बातचीत को उसी के अनुसार मापा जाता है — अभिवादन, अनुपालन, टोन, समाधान।" },
      { title: "सीरियल नंबर से डिवाइस असाइनमेंट", text: "स्टाफ़ को सीरियल नंबर और ऐप ID के साथ पेंडेंट असाइन करें। मैपिंग स्वतः आपके लोकल सर्वर पर सिंक हो जाती है।" },
      { title: "आर्किटेक्चर द्वारा गोपनीयता", text: "आवाज़ कभी क्लाउड से नहीं गुज़रती। अपलिंक केवल स्कोर ले जाता है; डाउनलिंक केवल SOP ले जाता है। डिज़ाइन से ही ऑडिटेबल।" },
      { title: "प्रति-कर्मचारी प्रगति", text: "हर टीम सदस्य के लिए ट्रेंड लाइनें, अनुपालन विश्लेषण और सत्र इतिहास, जैसे-जैसे लोकल सर्वर रिपोर्ट करता है अपडेट होते रहते हैं।" },
    ],
  },
  aiCoaching: {
    eyebrow: "स्कोर से आगे",
    heading: "AI कोचिंग सिफ़ारिशें, न कि सिर्फ़ एक संख्या।",
    intro: "हर डैशबोर्ड सत्र में उसी SOP से जनरेट किया गया एक सरल-भाषा कोचिंग नोट शामिल होता है जिससे स्कोर मापा गया था — क्या अच्छा रहा, अगली बार क्या सुधारना है, और क्यों। मैनेजरों को बातचीत के लिए एक शुरुआती बिंदु मिलता है, सिर्फ़ एक ग्रेड नहीं।",
    bullets: [
      "प्रति सत्र जनरेट होता है, उसी सटीक SOP चरण पर आधारित जो छूटा या अच्छी तरह किया गया — कभी सामान्य सलाह नहीं।",
      "एक हफ़्ते के पैटर्न को सामने लाता है, सिर्फ़ एक कॉल को नहीं — दोहराई जाने वाली कमियाँ अपने आप कोचिंग प्राथमिकता बन जाती हैं।",
      "आपके अपने बार-बार होने वाले गैप्स के आधार पर कस्टम ट्रेनिंग मॉड्यूल बनाए जा सकते हैं — अपने फ़्लोर के लिए तैयार ट्रेनिंग ट्रैक के बारे में हमसे पूछें।",
    ],
    cardLabel: "AI कोचिंग नोट",
    notes: [
      { name: "राम · सिक्योरिटी", body: "अभिवादन गर्मजोशी भरा और समय पर था। सत्यापन चरण पर समाधान अटक गया — इस हफ़्ते बंद करने से पहले एक बार फिर ID-चेक स्क्रिप्ट दोहराएं।" },
      { name: "प्रिया · फ्रंट डेस्क", body: "पूरे समय SOP का मजबूत अनुपालन रहा। जब मेहमान ने बिलिंग विवाद उठाया तो टोन में गिरावट आई — अगले पीक वीकेंड से पहले एक संक्षिप्त डी-एस्केलेशन रिफ्रेशर मददगार होगा।" },
      { name: "अर्जुन · कॉन्सियर्ज", body: "समाधान और समापन उत्कृष्ट थे — अनुरोध मेहमान को स्पष्ट रूप से पुष्टि किया गया। पहले दस सेकंड में अभिवादन जल्दबाजी में था; अनुरोध पर जाने से पहले धीमा करें।" },
    ],
  },
  replay: {
    eyebrow: "सबूत देखें",
    heading: "बातचीत को फिर से देखें, सिर्फ़ स्कोर नहीं।",
    intro: "हर सत्र का स्कोर उस ट्रांसक्रिप्ट के विरुद्ध चरण-दर-चरण विभाजित होता है जिसने इसे उत्पन्न किया — ताकि कोचिंग बातचीत सबूत से शुरू हो, कभी अनुमान से नहीं।",
    playerLabel: "नमूना सत्र · फ्रंट डेस्क",
    steps: [
      { time: "00:04", label: "अभिवादन", note: "गर्मजोशी भरी शुरुआत, 8 सेकंड में मेहमान का नाम इस्तेमाल किया गया।", score: 96 },
      { time: "00:41", label: "SOP अनुपालन", note: "सत्यापन चरण छूट गया — कमरा आवंटन से पहले पासपोर्ट नहीं मांगा गया।", score: 58 },
      { time: "02:15", label: "टोन और शिष्टाचार", note: "मेहमान की आवाज़ ऊंची होने के बाद भी पूरे समय धैर्यवान और विनम्र रहे।", score: 91 },
      { time: "03:02", label: "समाधान", note: "अनुरोध हल हुआ और मेहमान को स्पष्ट रूप से पुष्टि की गई।", score: 88 },
    ],
    disclaimer: "उदाहरण नमूना — यहाँ या क्लाउड में कहीं भी कोई वास्तविक मेहमान ऑडियो या ट्रांसक्रिप्ट नहीं दिखाया गया है।",
  },
  privacySection: {
    eyebrow: "गोपनीयता मॉडल",
    heading: "क्लाउड सिर्फ़ संख्याएं देखता है।",
    headingAccent: "केवल आपकी बिल्डिंग आवाज़ें सुनती है।",
    bullets: [
      "SOP दस्तावेज़ कंसोल से लोकल सर्वर तक एक प्रामाणिक, की-प्रोटेक्टेड चैनल के ज़रिए सिंक होते हैं।",
      "लोकल मूल्यांकन इंजन आपकी SOP के विरुद्ध पूरी तरह ऑफ़लाइन चलता है — स्कोर करने के लिए इंटरनेट की ज़रूरत नहीं।",
      "केवल संरचित स्कोर पेलोड ऊपर की ओर जाते हैं। कोई ट्रांसक्रिप्ट नहीं, कोई रिकॉर्डिंग नहीं, कोई अपवाद नहीं।",
      "हर वर्कस्पेस टेनेंट-आइसोलेटेड है; ब्रूट-फ़ोर्स सुरक्षा और हैश्ड क्रेडेंशियल्स कंसोल की रक्षा करते हैं।",
    ],
    readFull: "पूरी गोपनीयता नीति पढ़ें",
    diagramHint: "यह देखने के लिए कि क्या भेजा और प्राप्त किया जाता है, किसी घटक पर क्लिक करें",
    arrows: { sops: "SOP", scores: "स्कोर", recordings: "रिकॉर्डिंग" },
    premisesBoundary: "परिसर सीमा",
    voiceNeverCrosses: "आवाज़ का डेटा कभी इस सीमा को पार नहीं करता",
    nodes: {
      cloud: { label: "EXOPILOT क्लाउड कंसोल", detail: "स्कोर · SOP · डिवाइस रजिस्ट्री", sends: "SOP दस्तावेज़, कर्मचारी-डिवाइस मैपिंग (आपके लोकल सर्वर तक)", receives: "केवल संख्यात्मक गुणवत्ता स्कोर, साथ ही डिवाइस बैटरी/लास्ट-सीन टेलीमेट्री" },
      server: { label: "आपका लोकल सर्वर", detail: "आपकी SOP के विरुद्ध ऑफ़लाइन AI स्कोरिंग", sends: "गुणवत्ता स्कोर, सत्र मेटाडेटा, डिवाइस टेलीमेट्री (क्लाउड तक)", receives: "पेंडेंट/फ़ोन ऐप से रिकॉर्डिंग; क्लाउड से SOP" },
      device: { label: "पेंडेंट + फ़ोन ऐप", detail: "सेवा फ़्लोर पर BLE पेयरिंग", sends: "कच्चा ऑडियो, ब्लूटूथ लो एनर्जी के ज़रिए, केवल जोड़े गए फ़ोन ऐप को", receives: "क्लाउड से कुछ नहीं — यह कभी सीधे इंटरनेट से बात नहीं करता" },
    },
  },
  trustCenterTeaser: {
    eyebrow: "सुरक्षा और खरीद टीमों के लिए",
    heading: "सुरक्षा और ट्रस्ट सेंटर",
    text: "एन्क्रिप्शन, टेनेंट आइसोलेशन, एक्सेस कंट्रोल, ऑडिट लॉगिंग और हमारी भेद्यता रिपोर्टिंग प्रक्रिया — जो भी एक एंटरप्राइज़ समीक्षा माँगती है, एक ही जगह।",
    cta: "ट्रस्ट सेंटर देखें",
  },
  trustStats: [["0", "क्लाउड में कभी संग्रहीत आवाज़ फ़ाइलें"], ["100%", "स्कोरिंग आपके परिसर पर चलती है"], ["4", "मापे गए SOP अनुपालन आयाम"], ["<1 मिनट", "डिवाइस असाइनमेंट से लोकल सिंक तक"]],
  whereWeOperate: {
    eyebrow: "हम कहाँ काम करते हैं",
    heading: "भारत के सेवा फ़्लोर्स में विश्वसनीय।",
    text: "हेरिटेज होटलों से लेकर प्राइवेट बैंकिंग फ़्लोर तक — वहाँ चल रहे EXOPILOT वाली प्रॉपर्टी का प्रकार देखने के लिए किसी मार्कर पर होवर करें।",
    sampleNote: "नमूना स्थान दिखाए गए · उदाहरणात्मक",
  },
  dimensions: {
    eyebrow: "क्या मापा जाता है",
    heading: "चार आयाम।",
    headingAccent: "एक प्रकाशित मानक।",
    intro: "हर सत्र को आपके विभाग की SOP से प्राप्त उन्हीं चार आयामों के विरुद्ध स्कोर किया जाता है। कर्मचारी वही मानदंड देखते हैं जो उनके कोच देखते हैं — कुछ भी छिपा नहीं, कुछ भी मनमाना नहीं।",
    items: [
      { name: "अभिवादन और स्वागत", score: 92, desc: "क्या बातचीत आपके मानक के अनुसार शुरू हुई — अभिवादन, नाम का उपयोग, पहले तीस सेकंड में गर्मजोशी।" },
      { name: "SOP अनुपालन", score: 87, desc: "विभागीय प्रक्रिया का चरण-दर-चरण अनुपालन, सत्यापन से लेकर हैंड-ऑफ तक।" },
      { name: "टोन और शिष्टाचार", score: 90, desc: "पूरी बातचीत में विनम्रता, धैर्य और गति बनाए रखी गई — सिर्फ़ पहले मिनट में नहीं।" },
      { name: "समाधान और समापन", score: 84, desc: "क्या ग्राहक की ज़रूरत वास्तव में पूरी हुई, और क्या बातचीत आपके समापन मानक के अनुसार बंद हुई।" },
    ],
  },
  hotels: {
    eyebrow: "उपयोग मामला · आतिथ्य",
    heading: "उन होटलों के लिए बनाया गया जहाँ हर मेहमान इंटरैक्शन ही ब्रांड है।",
    intro: "आगमन से चेक-आउट तक, EXOPILOT होटल ऑपरेटरों को फ्रंट डेस्क, कॉन्सियर्ज, हाउसकीपिंग और F&B में वही सबूत-आधारित कोचिंग लूप देता है — बिना किसी एक रिकॉर्डिंग के प्रॉपर्टी से बाहर गए।",
    cards: [
      { title: "फ्रंट डेस्क और आगमन", text: "हर चेक-इन को आपके स्वागत मानक के विरुद्ध स्कोर किया जाता है — अभिवादन, अपसेल ऑफ़र, रूम-ओरिएंटेशन स्क्रिप्ट — ताकि मेहमान के पहले नब्बे सेकंड हर शिफ़्ट में एक जैसे रहें।" },
      { title: "कॉन्सियर्ज और मेहमान अनुरोध", text: "समाधान समय और फ़ॉलो-थ्रू को आपकी SOP के विरुद्ध ट्रैक किया जाता है, ताकि कोई वादा की गई रिज़र्वेशन या सुविधा अनुरोध कभी चुपचाप छूट न जाए।" },
      { title: "F&B और इन-रूम डाइनिंग", text: "ऑर्डर सटीकता, एलर्जन-चेक अनुपालन और टोन को प्रॉपर्टी के हर आउटलेट में उसी तरह मापा जाता है, न कि केवल फ्लैगशिप रेस्तरां में।" },
    ],
    valueLabel: "व्यावसायिक मामला",
    values: [
      { stat: "+9 अंक", text: "एक बार कोचिंग किस्से से सबूत की ओर बढ़ने पर, एक तिमाही में फ्रंट-डेस्क स्कोर में सामान्य सुधार।" },
      { stat: "0", text: "मेहमान बातचीत जो कभी प्रॉपर्टी से बाहर जाती हैं — कानूनी टीम या गोपनीयता-सचेत मेहमान के लिए चिंता की कोई बात नहीं।" },
      { stat: "1 SOP", text: "प्रति विभाग, वर्ज़नयुक्त — हर नई नियुक्ति को उसी सटीक मानक पर मापा जाता है जिसे आपके सर्वश्रेष्ठ प्रदर्शनकर्ता पहले से पूरा करते हैं।" },
    ],
    cta: "अपनी प्रॉपर्टी के बारे में हमसे बात करें",
  },
  rollout: {
    eyebrow: "तैनाती",
    heading: "खाली कंसोल से लाइव स्कोरिंग तक, दो दिनों में।",
    items: [
      { when: "दिन 1 · सुबह", what: "अपना कंसोल बनाएं, विभाग कॉन्फ़िगर करें और अपने SOP दस्तावेज़ अपलोड करें।" },
      { when: "दिन 1 · दोपहर", what: "साइट पर लोकल सर्वर स्थापित करें और साथी फ़ोन ऐप के ज़रिए पेंडेंट जोड़ें।" },
      { when: "दिन 2", what: "सीरियल नंबर से कर्मचारियों को डिवाइस असाइन करें — मैपिंग और SOP स्वतः सिंक होते हैं।" },
      { when: "दिन 3 से आगे", what: "हर शिफ़्ट के बाद स्कोर आने लगते हैं। कोचिंग बातचीत सबूत से शुरू होती है, किस्सों से नहीं।" },
    ],
  },
  testimonials: {
    eyebrow: "ऑपरेटर क्या कहते हैं",
    heading: "जहाँ एक बातचीत भी ब्रांड को आगे बढ़ाती है, वहाँ भरोसेमंद।",
    items: [
      { quote: "हम आखिरकार किस्सों की बजाय सबूत के साथ कोचिंग करते हैं। एक तिमाही में फ्रंट-डेस्क स्कोर नौ अंक बढ़े — और हमारे मेहमानों की बातचीत कभी प्रॉपर्टी से बाहर नहीं गई।", name: "रुक्मिणी वर्मा", role: "जनरल मैनेजर · हेरिटेज पैलेस होटल, कोच्चि" },
      { quote: "फाइन ज्वेलरी में, एक खराब तरीके से संभाली गई सलाह तीन पीढ़ियों के रिश्ते की कीमत चुका सकती है। EXOPILOT हमें ठीक-ठीक दिखाता है कि बातचीत कहाँ हमारे मानक से भटकती है।", name: "अर्जुन मेहता", role: "रिटेल डायरेक्टर · फाइन ज्वेलरी चेन, 40 शोरूम" },
      { quote: "कानूनी टीम ने एक हफ़्ते में मंज़ूरी दे दी। क्लाउड में संख्याओं के अलावा समीक्षा करने के लिए कुछ भी नहीं है — यही सरलता पूरा ऑडिट है।", name: "क्लेयर डुबॉय", role: "हेड ऑफ़ कम्प्लायंस · प्राइवेट बैंकिंग समूह" },
    ],
  },
  faq: {
    eyebrow: "प्रश्न",
    heading: "हर ऑपरेशंस और कम्प्लायंस टीम द्वारा पूछे गए।",
    introPrefix: "कुछ और विशिष्ट? ",
    writeToUs: "हमें लिखें",
    orRead: " या ",
    fullPolicy: "पूरी गोपनीयता नीति पढ़ें",
    items: [
      { q: "क्या कभी कोई ऑडियो क्लाउड तक पहुँचता है?", a: "नहीं। क्लाउड कोई भी ऐसा एंडपॉइंट उजागर नहीं करता जो ऑडियो प्राप्त कर सके। रिकॉर्डिंग पेंडेंट से BLE के ज़रिए साथी फ़ोन ऐप तक जाती है और आपके ऑन-प्रिमाइस सर्वर पर समाप्त होती है। अपलिंक केवल संरचित संख्यात्मक स्कोर के लिए मान्य है — ऐसा कोई कॉन्फ़िगरेशन नहीं है जिसमें आवाज़ का डेटा अपलोड किया जा सके।" },
      { q: "कर्मचारी वास्तव में क्या पहनते हैं?", a: "एक हल्का पेंडेंट जिसमें पूरी शिफ़्ट की बैटरी होती है, जो एक सामान्य फ़ोन पर साथी ऐप से जुड़ा होता है। बैटरी स्तर और स्थिति आपके कंसोल में दिखाई देती है।" },
      { q: "अगर हमारा इंटरनेट बंद हो जाए तो क्या होगा?", a: "स्कोरिंग बिना रुके जारी रहती है — मूल्यांकन इंजन आपके लोकल सर्वर पर पूरी तरह ऑफ़लाइन, अंतिम सिंक की गई SOP के विरुद्ध चलता है। स्कोर लोकल रूप से क्यू होते हैं और कनेक्टिविटी वापस आने पर आपके कंसोल से सिंक होते हैं। सेवा फ़्लोर पर कुछ भी क्लाउड पर निर्भर नहीं है।" },
      { q: "हम इसे स्टाफ़ के लिए निष्पक्ष और कानूनी कैसे रखें?", a: "EXOPILOT मानदंड-आधारित कोचिंग के लिए बनाया गया है, गुप्त निगरानी के लिए नहीं। हम तैनाती करने वाले संगठनों से कर्मचारियों को सूचित करने, स्थानीय कानून द्वारा आवश्यक कोई भी सहमति प्राप्त करने, और जिन SOP के विरुद्ध उन्हें मापा जाता है उन्हें साझा करने की अपेक्षा करते हैं। हर स्कोर एक प्रकाशित, वर्ज़नयुक्त मानक तक वापस जाता है।" },
      { q: "क्या हम रोलआउट के बाद SOP अपडेट कर सकते हैं?", a: "हाँ। किसी भी समय SOP लाइब्रेरी में एक नया वर्ज़न अपलोड करें; यह अगले चक्र में आपके लोकल सर्वर पर सिंक होता है और बाद के सत्र उसी के विरुद्ध स्कोर किए जाते हैं। हर विभाग एक सक्रिय, वर्ज़नयुक्त SOP रखता है ताकि लागू मानक में कभी अस्पष्टता न हो।" },
      { q: "तैनाती में कितना समय लगता है?", a: "अधिकांश सिंगल-साइट तैनाती दो दिनों के भीतर लाइव इंटरैक्शन स्कोर करना शुरू कर देती हैं: पहले दिन कंसोल सेटअप और SOP अपलोड, उसी दिन लोकल सर्वर और पेंडेंट पेयरिंग, दूसरे दिन डिवाइस असाइनमेंट। मल्टी-साइट रोलआउट हर प्रॉपर्टी के लिए उसी पैटर्न को दोहराते हैं।" },
    ],
  },
  finalCta: { heading: "अपने सेवा फ़्लोर पर मापने योग्य मानक लाएं।", text: "अपना कंसोल सेटअप करें, अपनी टीम जोड़ें और कुछ ही मिनटों में अपनी पहली SOP सिंक करें।", ctaGetStarted: "शुरू करें", ctaBookDemo: "डेमो बुक करें" },
  contact: {
    eyebrow: "संपर्क में रहें",
    heading: "अपने फ़्लोर के बारे में हमसे बात करें।",
    text: "हमें अपनी प्रॉपर्टी, शोरूम या शाखाओं के बारे में बताएं और हम आपको रोलआउट तय करने में मदद करेंगे।",
    nameLabel: "नाम", namePlaceholder: "जेन डो",
    emailLabel: "कार्य ईमेल", emailPlaceholder: "you@company.com",
    messageLabel: "संदेश", messagePlaceholder: "हमें अपने फ़्लोर, साइटों और समयसीमा के बारे में बताएं।",
    send: "संदेश भेजें", sending: "भेजा जा रहा है…",
    toastTitle: "संदेश भेजा गया", toastDesc: "हम एक व्यावसायिक दिन के भीतर आपसे संपर्क करेंगे।",
  },
  bookDemo: {
    title: "अपना डेमो बुक करें",
    desc: "हमारी टीम के साथ 30 मिनट — कंसोल, लोकल सर्वर और पेंडेंट को क्रियान्वित देखें।",
    nameLabel: "नाम", companyLabel: "कंपनी", emailLabel: "कार्य ईमेल", phoneLabel: "फ़ोन (वैकल्पिक)",
    submit: "स्लॉट का अनुरोध करें", submitting: "भेजा जा रहा है…",
    toastTitle: "डेमो का अनुरोध किया गया", toastDesc: "हम एक व्यावसायिक दिन के भीतर संपर्क करेंगे।",
  },
  stickyCta: { label: "डेमो बुक करें" },
  chatbot: {
    launcherLabel: "हमसे चैट करें",
    title: "EXOPILOT सहायक",
    subtitle: "प्रोडक्ट, गोपनीयता या मूल्य निर्धारण के बारे में पूछें",
    placeholder: "एक प्रश्न पूछें…",
    send: "भेजें",
    greeting: "नमस्ते! मैं EXOPILOT के बारे में प्रश्नों के उत्तर दे सकता हूँ — पेंडेंट कैसे काम करता है, क्या ऑन-प्रिमाइस रहता है, मूल्य निर्धारण, तैनाती। आप क्या जानना चाहेंगे?",
    notConfigured: "इस डिप्लॉयमेंट पर AI सहायक अभी कनेक्ट नहीं है — कृपया नीचे दिए गए संपर्क फ़ॉर्म का उपयोग करें या hello@exopilot.io पर लिखें, कोई व्यक्ति आपसे संपर्क करेगा।",
    error: "सहायक तक पहुँचने में कुछ गड़बड़ी हुई — कृपया फिर से प्रयास करें, या नीचे दिए गए संपर्क फ़ॉर्म का उपयोग करें।",
    offTopic: "मैं केवल EXOPILOT से जुड़े प्रश्नों में मदद कर सकता हूँ — प्रोडक्ट, गोपनीयता मॉडल, तैनाती, या मूल्य निर्धारण। किसी और चीज़ के लिए, कृपया संपर्क फ़ॉर्म का उपयोग करें।",
  },
  footer: { howItWorks: "यह कैसे काम करता है", platform: "प्लेटफ़ॉर्म", contact: "संपर्क करें", trust: "सुरक्षा और ट्रस्ट", privacy: "गोपनीयता नीति", cookies: "कुकीज़ नीति", terms: "सेवा की शर्तें", tagline: "© 2026 EXOPILOT · सेवा गुणवत्ता इंटेलिजेंस · ऑन-प्रिमाइस वॉइस प्रोसेसिंग" },
};

const ar = {
  nav: { how: "كيف يعمل", platform: "المنصة", privacy: "الخصوصية", trust: "مركز الثقة", contact: "تواصل معنا", signIn: "تسجيل الدخول", getStarted: "ابدأ الآن", bookDemo: "احجز عرضًا توضيحيًا" },
  hero: {
    eyebrow: "ذكاء جودة الخدمة",
    titleLine: "قِس أداء فريقك.",
    titleAccent: "دون أن يغادر الصوت مبانيك أبدًا.",
    subtitle: "يقيّم EXOPILOT فرق الخدمة لديك وفق إجراءات التشغيل القياسية الخاصة بك — معالجة محلية، ورؤية سحابية.",
    ctaConsole: "افتح لوحة التحكم الخاصة بك",
    ctaArchitecture: "شاهد البنية المعمارية",
    ctaBookDemo: "احجز عرضك التوضيحي",
    ctaWatchFilm: "شاهد كيف يعمل EXOPILOT",
    liveFloorView: "عرض مباشر للأداء",
    loopPremises: "منشأتك",
    loopConsole: "درجة لوحة التحكم",
    loopCaption: "لا يغادر مبناك سوى الدرجة — الصوت لا يغادر أبدًا.",
    loopStages: [
      "يسجل المعلّق المحادثة",
      "يُرسل إلى خادمك المحلي عبر BLE",
      "يُقيَّم وفق إجراء التشغيل القياسي لديك — تصل الدرجة 94.2 إلى لوحة التحكم",
    ],
    loopPendantLabel: "المعلّق",
    loopBleLabel: "BLE",
    loopServerLabel: "الخادم المحلي",
    loopSuggestionLabel: "اقتراح الذكاء الاصطناعي",
    loopSuggestionText: "\u201cبالتأكيد، سأتحقق من ذلك من أجلك.\u201d",
    deviceBadge: "تسجيل محلي، الآن",
  },
  trustChips: ["الصوت لا يغادر مبانيك أبدًا", "رفع بيانات النقاط فقط إلى السحابة", "مزامنة مشفّرة لإجراءات التشغيل القياسية", "عزل صارم بين المستأجرين"],
  industriesLabel: "مصمّم للمساحات التي تُعد فيها الخدمة هي المنتج",
  industries: ["فنادق فاخرة", "تجارة المجوهرات", "الخدمات المصرفية الخاصة", "المطاعم الراقية", "المتاجر الرئيسية", "مكاتب الاستقبال"],
  heroStats: [["94.2", "متوسط درجة الخدمة"], ["0", "ملفات صوتية في السحابة"], ["100%", "تغطية إجراءات التشغيل القياسية"]],
  how: {
    eyebrow: "كيف يعمل",
    heading: "حلقة مغلقة بين أرضية الخدمة لديك وخادمك المحلي ولوحة التحكم الخاصة بك.",
    steps: [
      { n: "01", title: "القلادة تسجّل كل تفاعل", text: "يرتدي كل موظف قلادة EXOPILOT الأنيقة التي تسجّل تفاعلات العملاء وتبثّها عبر البلوتوث منخفض الطاقة إلى تطبيق الهاتف المرافق." },
      { n: "02", title: "تطبيق الهاتف ينقل البيانات إلى خادمك المحلي", text: "يرسل التطبيق التسجيلات إلى خادمك الموجود في الموقع، حيث يقوم محرك ذكاء اصطناعي يعمل دون اتصال بالإنترنت بتقييم كل تفاعل وفق إجراءات التشغيل القياسية لديك — لا يغادر أي صوت المبنى أبدًا." },
      { n: "03", title: "النقاط فقط تصل إلى السحابة", text: "تُزامن نقاط الجودة الرقمية صعودًا إلى لوحة معلوماتك السحابية. تُزامن إجراءات التشغيل القياسية نزولًا. تبقى بيانات الصوت محلية دائمًا." },
    ],
    videoTitle: "شاهد كيف يعمل EXOPILOT",
    videoCaption: "فيلم المنتج · دقيقتان",
    videoBadge: "من القلادة إلى النتيجة — الحلقة الكاملة",
    videoToast: "فيلم منتج EXOPILOT الكامل قادم قريبًا",
    videoToastDesc: "في هذه الأثناء، افتح لوحة التحكم الخاصة بك واستكشف بيانات العرض التوضيحي المباشر.",
  },
  platform: {
    eyebrow: "المنصة",
    heading: "كل ما يحتاجه فريق الجودة لديك، ولا شيء يخشاه فريق الامتثال.",
    features: [
      { title: "تقييم قائم على إجراءات التشغيل القياسية", text: "قم برفع إجراء تشغيل قياسي واحد لكل قسم. يُقاس كل تفاعل وفقًا له — الترحيب، الالتزام، النبرة، حل المشكلة." },
      { title: "تعيين الأجهزة بالرقم التسلسلي", text: "عيّن القلادات للموظفين برقم تسلسلي ومعرّف تطبيق. تتم مزامنة الربط تلقائيًا مع خادمك المحلي." },
      { title: "خصوصية بحكم البنية المعمارية", text: "الصوت لا يمر عبر السحابة أبدًا. الرفع يحمل النقاط فقط؛ التنزيل يحمل إجراءات التشغيل القياسية فقط. قابل للتدقيق بالتصميم." },
      { title: "تقدّم لكل موظف", text: "خطوط اتجاه، تحليلات الالتزام، وسجلات الجلسات لكل عضو في الفريق، تُحدَّث مع كل تقرير من الخادم المحلي." },
    ],
  },
  aiCoaching: {
    eyebrow: "أبعد من النتيجة",
    heading: "توصيات تدريب بالذكاء الاصطناعي، وليست مجرد رقم.",
    intro: "تتضمن كل جلسة في لوحة المعلومات ملاحظة تدريب بلغة بسيطة، مُولَّدة من نفس إجراء التشغيل القياسي الذي قِيست النتيجة وفقًا له — ما سار بشكل جيد، وما يجب العمل عليه في المرة القادمة، ولماذا. يحصل المدراء على نقطة انطلاق للمحادثة، وليس مجرد درجة.",
    bullets: [
      "تُولَّد لكل جلسة، ومبنية على خطوة إجراء التشغيل القياسي الدقيقة التي فاتت أو نُفّذت جيدًا — لا نصائح عامة أبدًا.",
      "تُظهر النمط عبر أسبوع كامل، وليس مكالمة واحدة فقط — الفجوات المتكررة تصبح أولوية تدريب تلقائيًا.",
      "يمكن بناء وحدات تدريب مخصصة حول الفجوات المتكررة الخاصة بك — تواصل معنا لتصميم مسار تدريب مخصص لأرضية الخدمة لديك.",
    ],
    cardLabel: "ملاحظة تدريب بالذكاء الاصطناعي",
    notes: [
      { name: "رام · الأمن", body: "كان الترحيب دافئًا وفي الوقت المناسب. تعثّر حل المشكلة عند خطوة التحقق — راجع نص التحقق من الهوية مرة أخرى هذا الأسبوع قبل الانتقال إلى الإغلاق." },
      { name: "بريا · مكتب الاستقبال", body: "التزام قوي بإجراءات التشغيل القياسية طوال الوقت. انخفضت النبرة عندما أثار الضيف نزاعًا متعلقًا بالفاتورة — سيساعد تذكير قصير بتهدئة التوتر قبل عطلة نهاية الأسبوع المزدحمة القادمة." },
      { name: "أرجون · الاستقبال الخاص", body: "كان الحل والإغلاق ممتازين — تم تأكيد الطلب للضيف بوضوح. كان الترحيب متسرعًا في العشر ثوانٍ الأولى؛ تمهّل قبل الانتقال مباشرة إلى الطلب." },
    ],
  },
  replay: {
    eyebrow: "شاهد الدليل",
    heading: "أعد تشغيل المحادثة، وليس النتيجة فقط.",
    intro: "تتفكك نتيجة كل جلسة خطوة بخطوة مقابل النص الذي أنتجها — بحيث تبدأ محادثة التدريب من الدليل، وليس من التخمين أبدًا.",
    playerLabel: "جلسة نموذجية · مكتب الاستقبال",
    steps: [
      { time: "00:04", label: "الترحيب", note: "افتتاح دافئ، تم استخدام اسم الضيف خلال 8 ثوانٍ.", score: 96 },
      { time: "00:41", label: "الالتزام بإجراءات التشغيل القياسية", note: "تم تخطي خطوة التحقق — لم يُطلب جواز السفر قبل تخصيص الغرفة.", score: 58 },
      { time: "02:15", label: "النبرة والكياسة", note: "صبور ومهذب طوال الوقت، حتى بعد أن رفع الضيف صوته.", score: 91 },
      { time: "03:02", label: "حل المشكلة", note: "تم حل الطلب وتأكيده للضيف بوضوح.", score: 88 },
    ],
    disclaimer: "نموذج توضيحي — لا يُعرض هنا أو في أي مكان في السحابة أي صوت أو نص حقيقي لضيف.",
  },
  privacySection: {
    eyebrow: "نموذج الخصوصية",
    heading: "السحابة ترى الأرقام فقط.",
    headingAccent: "مبناك وحده يسمع الأصوات.",
    bullets: [
      "تتم مزامنة مستندات إجراءات التشغيل القياسية من لوحة التحكم إلى الخادم المحلي عبر قناة مصادَق عليها ومحمية بمفتاح.",
      "يعمل محرك التقييم المحلي دون اتصال بالإنترنت بالكامل وفق إجراءات التشغيل القياسية لديك — لا حاجة للإنترنت للتقييم.",
      "فقط حزم النقاط المهيكلة تنتقل صعودًا. لا نصوص، لا تسجيلات، بلا استثناءات.",
      "كل مساحة عمل معزولة عن غيرها؛ حماية من الهجمات العنيفة وبيانات اعتماد مُشفَّرة تحمي لوحة التحكم.",
    ],
    readFull: "اقرأ سياسة الخصوصية كاملة",
    diagramHint: "انقر على أحد المكونات لترى ما يُرسله ويستقبله",
    arrows: { sops: "إجراءات التشغيل القياسية", scores: "النقاط", recordings: "التسجيلات" },
    premisesBoundary: "حدود المبنى",
    voiceNeverCrosses: "بيانات الصوت لا تتجاوز هذا الخط أبدًا",
    nodes: {
      cloud: { label: "لوحة تحكم EXOPILOT السحابية", detail: "النقاط · إجراءات التشغيل القياسية · سجل الأجهزة", sends: "مستندات إجراءات التشغيل القياسية، وربط الموظف بالجهاز (نزولًا إلى خادمك المحلي)", receives: "نقاط جودة رقمية فقط، إضافة إلى بيانات بطارية الجهاز وآخر ظهور" },
      server: { label: "خادمك المحلي", detail: "تقييم بالذكاء الاصطناعي دون اتصال وفق إجراءات التشغيل القياسية لديك", sends: "نقاط الجودة، بيانات الجلسة، بيانات الجهاز (صعودًا إلى السحابة)", receives: "تسجيلات من القلادة/تطبيق الهاتف؛ إجراءات التشغيل القياسية من السحابة" },
      device: { label: "القلادة + تطبيق الهاتف", detail: "اقتران عبر البلوتوث منخفض الطاقة على أرضية الخدمة", sends: "صوت خام، عبر البلوتوث منخفض الطاقة، إلى تطبيق الهاتف المقترن فقط", receives: "لا شيء من السحابة — لا يتصل بالإنترنت مباشرة أبدًا" },
    },
  },
  trustCenterTeaser: {
    eyebrow: "لفرق الأمن والمشتريات",
    heading: "مركز الأمن والثقة",
    text: "التشفير، عزل المستأجرين، التحكم في الوصول، تسجيل التدقيق، وعملية الإبلاغ عن الثغرات لدينا — كل ما تطلبه مراجعة المؤسسات، في مكان واحد.",
    cta: "زيارة مركز الثقة",
  },
  trustStats: [["0", "ملفات صوتية خُزّنت في السحابة على الإطلاق"], ["100%", "من عمليات التقييم تجري في مبانيك"], ["4", "أبعاد التزام بإجراءات التشغيل القياسية يتم قياسها"], ["<1 دقيقة", "من تعيين الجهاز إلى المزامنة المحلية"]],
  whereWeOperate: {
    eyebrow: "أين نعمل",
    heading: "موثوق به عبر مساحات الخدمة في الهند.",
    text: "من الفنادق التراثية إلى قاعات الخدمات المصرفية الخاصة — مرّر المؤشر فوق علامة لترى نوع المنشأة التي تشغّل EXOPILOT هناك.",
    sampleNote: "مواقع نموذجية معروضة · توضيحية",
  },
  dimensions: {
    eyebrow: "ما الذي يتم قياسه",
    heading: "أربعة أبعاد.",
    headingAccent: "معيار واحد منشور.",
    intro: "تُقيَّم كل جلسة وفق نفس الأبعاد الأربعة، المستمدة من إجراء التشغيل القياسي لقسمك. يرى الموظفون نفس المعايير التي يراها مدربوهم — لا شيء مخفي، ولا شيء عشوائي.",
    items: [
      { name: "الترحيب والاستقبال", score: 92, desc: "هل بدأ التفاعل وفق معيارك — التحية، استخدام الاسم، الدفء في الثلاثين ثانية الأولى." },
      { name: "الالتزام بإجراءات التشغيل القياسية", score: 87, desc: "الالتزام خطوة بخطوة بإجراء القسم، من التحقق وحتى التسليم." },
      { name: "النبرة والكياسة", score: 90, desc: "الحفاظ على الأدب والصبر والإيقاع طوال المحادثة كاملة — وليس في الدقيقة الأولى فقط." },
      { name: "الحل والإغلاق", score: 84, desc: "هل تمت تلبية حاجة العميل فعليًا، وهل أُغلق التفاعل وفق معيار الإغلاق لديك." },
    ],
  },
  hotels: {
    eyebrow: "حالة استخدام · الضيافة",
    heading: "مصمَّم للفنادق حيث يُعد كل تفاعل مع الضيف هو العلامة التجارية.",
    intro: "من الوصول إلى المغادرة، يمنح EXOPILOT مشغلي الفنادق نفس حلقة التدريب القائمة على الأدلة عبر الاستقبال، والاستقبال الخاص، وخدمة الغرف، والمطاعم — دون أن يغادر أي تسجيل المنشأة على الإطلاق.",
    cards: [
      { title: "مكتب الاستقبال والوصول", text: "يُقيَّم كل تسجيل وصول وفق معيار الترحيب لديك — التحية، عرض البيع الإضافي، نص توجيه الغرفة — بحيث تكون أول تسعين ثانية للضيف متسقة في كل نوبة عمل." },
      { title: "الاستقبال الخاص وطلبات الضيوف", text: "يُتابَع وقت الحل والمتابعة وفق إجراء التشغيل القياسي لديك، بحيث لا يضيع حجز أو طلب خدمة مُوعود به بصمت أبدًا." },
      { title: "المطاعم وخدمة الغرف", text: "دقة الطلبات، والالتزام بفحص الحساسية، والنبرة تُقاس بنفس الطريقة عبر كل منفذ في المنشأة، وليس المطعم الرئيسي فقط." },
    ],
    valueLabel: "الجدوى التجارية",
    values: [
      { stat: "+9 نقاط", text: "تحسّن نموذجي في نتيجة مكتب الاستقبال خلال ربع سنة واحد بمجرد أن ينتقل التدريب من الحكايات إلى الأدلة." },
      { stat: "0", text: "محادثات الضيوف التي تغادر المنشأة على الإطلاق — لا شيء يقلق الفريق القانوني أو الضيف الحريص على خصوصيته." },
      { stat: "إجراء واحد", text: "لكل قسم، بإصدارات مُرقَّمة — يُقاس كل موظف جديد وفق نفس المعيار الدقيق الذي يستوفيه أفضل موظفيك بالفعل." },
    ],
    cta: "تحدث معنا عن منشأتك",
  },
  rollout: {
    eyebrow: "النشر",
    heading: "من لوحة تحكم فارغة إلى تقييم مباشر خلال يومين.",
    items: [
      { when: "اليوم 1 · صباحًا", what: "أنشئ لوحة التحكم الخاصة بك، وقم بتكوين الأقسام، وارفع مستندات إجراءات التشغيل القياسية." },
      { when: "اليوم 1 · مساءً", what: "قم بتشغيل الخادم المحلي في الموقع واقرن القلادات عبر تطبيق الهاتف المرافق." },
      { when: "اليوم 2", what: "عيّن الأجهزة للموظفين بالرقم التسلسلي — يتم مزامنة الربط وإجراءات التشغيل القياسية تلقائيًا." },
      { when: "من اليوم 3 فصاعدًا", what: "تتدفق النقاط بعد كل نوبة عمل. تبدأ محادثات التدريب بالأدلة، وليس بالحكايات." },
    ],
  },
  testimonials: {
    eyebrow: "ماذا يقول المشغّلون",
    heading: "موثوق به حيث تحمل محادثة واحدة العلامة التجارية بأكملها.",
    items: [
      { quote: "أخيرًا أصبحنا ندرّب بالأدلة بدلًا من الحكايات. ارتفعت نتائج مكتب الاستقبال تسع نقاط في ربع سنة واحد — ولم تغادر محادثات ضيوفنا المنشأة أبدًا.", name: "روكميني فارما", role: "المدير العام · فندق تراثي تاريخي، كوتشي" },
      { quote: "في تجارة المجوهرات الفاخرة، يمكن لاستشارة واحدة سيئة الإدارة أن تكلف علاقة امتدت ثلاثة أجيال. يُظهر لنا EXOPILOT بالضبط أين تنحرف المحادثات عن معيارنا.", name: "أرجون ميهتا", role: "مدير التجزئة · سلسلة مجوهرات فاخرة، 40 صالة عرض" },
      { quote: "وافق الفريق القانوني خلال أسبوع. لا يوجد شيء لمراجعته في السحابة سوى الأرقام — هذه البساطة هي التدقيق بأكمله.", name: "كلير دوبوا", role: "رئيسة الامتثال · مجموعة خدمات مصرفية خاصة" },
    ],
  },
  faq: {
    eyebrow: "الأسئلة",
    heading: "التي يطرحها كل فريق عمليات وامتثال.",
    introPrefix: "لديك سؤال أكثر تحديدًا؟ ",
    writeToUs: "راسلنا",
    orRead: " أو اقرأ ",
    fullPolicy: "سياسة الخصوصية كاملة",
    items: [
      { q: "هل يصل أي صوت إلى السحابة على الإطلاق؟", a: "لا. لا تكشف السحابة عن أي نقطة نهاية قادرة على استقبال الصوت. تنتقل التسجيلات من القلادة عبر البلوتوث منخفض الطاقة إلى تطبيق الهاتف المرافق وتنتهي عند خادمك الموجود في الموقع. يُتحقق من صحة الرفع ليكون نقاطًا رقمية مهيكلة فقط — لا يوجد أي إعداد يمكن من خلاله رفع بيانات صوتية." },
      { q: "ماذا يرتدي الموظفون فعليًا؟", a: "قلادة خفيفة الوزن ببطارية تكفي لنوبة عمل كاملة، مقترنة بتطبيق مرافق على هاتف عادي. مستوى البطارية والحالة مرئيان في لوحة التحكم الخاصة بك." },
      { q: "ماذا يحدث إذا انقطع اتصالنا بالإنترنت؟", a: "يستمر التقييم دون انقطاع — يعمل محرك التقييم دون اتصال بالإنترنت بالكامل على خادمك المحلي وفق آخر إجراءات التشغيل القياسية المُزامنة. تُصفّ النقاط محليًا وتُزامن مع لوحة التحكم الخاصة بك بمجرد عودة الاتصال. لا شيء في أرضية الخدمة يعتمد على السحابة." },
      { q: "كيف نحافظ على عدالة وقانونية هذا للموظفين؟", a: "صُمّم EXOPILOT للتدريب القائم على معايير محددة، وليس للمراقبة الخفية. نطلب من المؤسسات التي تنشره إبلاغ الموظفين، والحصول على أي موافقة يتطلبها القانون المحلي، ومشاركة إجراءات التشغيل القياسية التي يُقاسون وفقها. كل نتيجة تعود إلى معيار منشور ومُرقَّم بإصدار." },
      { q: "هل يمكننا تحديث إجراءات التشغيل القياسية بعد النشر؟", a: "نعم. ارفع إصدارًا جديدًا إلى مكتبة إجراءات التشغيل القياسية في أي وقت؛ تتم مزامنته مع خادمك المحلي في الدورة التالية، وتُقيَّم الجلسات اللاحقة وفقًا له. يحتفظ كل قسم بإجراء تشغيل قياسي واحد نشط ومُرقَّم بإصدار حتى لا يكون هناك غموض أبدًا بشأن المعيار المعمول به." },
      { q: "كم من الوقت يستغرق النشر؟", a: "تبدأ معظم عمليات النشر لموقع واحد في تقييم التفاعلات المباشرة خلال يومين: إعداد لوحة التحكم ورفع إجراءات التشغيل القياسية في اليوم الأول، وتشغيل الخادم المحلي واقتران القلادة في نفس اليوم، وتعيين الأجهزة في اليوم الثاني. تكرر عمليات النشر متعددة المواقع نفس النمط لكل منشأة." },
    ],
  },
  finalCta: { heading: "اجلب معايير قابلة للقياس إلى أرضية الخدمة لديك.", text: "أعدّ لوحة التحكم الخاصة بك، وأضف فريقك، وزامن أول إجراء تشغيل قياسي لديك خلال دقائق.", ctaGetStarted: "ابدأ الآن", ctaBookDemo: "احجز عرضًا توضيحيًا" },
  contact: {
    eyebrow: "تواصل معنا",
    heading: "تحدث معنا عن أرضية الخدمة لديك.",
    text: "أخبرنا عن منشآتك أو صالات العرض أو الفروع وسنساعدك في تحديد نطاق النشر.",
    nameLabel: "الاسم", namePlaceholder: "جين دو",
    emailLabel: "البريد الإلكتروني للعمل", emailPlaceholder: "you@company.com",
    messageLabel: "الرسالة", messagePlaceholder: "أخبرنا عن أرضية الخدمة، والمواقع، والجدول الزمني لديك.",
    send: "إرسال الرسالة", sending: "جارٍ الإرسال…",
    toastTitle: "تم إرسال الرسالة", toastDesc: "سنعاود التواصل معك خلال يوم عمل واحد.",
  },
  bookDemo: {
    title: "احجز عرضك التوضيحي",
    desc: "30 دقيقة مع فريقنا — شاهد لوحة التحكم، والخادم المحلي، والقلادة أثناء العمل.",
    nameLabel: "الاسم", companyLabel: "الشركة", emailLabel: "البريد الإلكتروني للعمل", phoneLabel: "الهاتف (اختياري)",
    submit: "اطلب موعدًا", submitting: "جارٍ الإرسال…",
    toastTitle: "تم طلب العرض التوضيحي", toastDesc: "سنتواصل معك خلال يوم عمل واحد.",
  },
  stickyCta: { label: "احجز عرضًا توضيحيًا" },
  chatbot: {
    launcherLabel: "تحدث معنا",
    title: "مساعد EXOPILOT",
    subtitle: "اسأل عن المنتج أو الخصوصية أو التسعير",
    placeholder: "اطرح سؤالاً…",
    send: "إرسال",
    greeting: "مرحبًا! يمكنني الإجابة عن أسئلة حول EXOPILOT — كيف تعمل القلادة، وما الذي يبقى في الموقع، والتسعير، والنشر. ماذا تود أن تعرف؟",
    notConfigured: "لم يتم توصيل المساعد الذكي بعد على هذا النشر — يرجى استخدام نموذج التواصل أدناه أو مراسلة hello@exopilot.io وسيتواصل معك أحدهم.",
    error: "حدث خطأ ما أثناء الوصول إلى المساعد — يرجى المحاولة مرة أخرى، أو استخدام نموذج التواصل أدناه.",
    offTopic: "يمكنني فقط المساعدة في الأسئلة المتعلقة بـ EXOPILOT نفسه — المنتج، نموذج الخصوصية، النشر، أو التسعير. لأي شيء آخر، يرجى استخدام نموذج التواصل.",
  },
  footer: { howItWorks: "كيف يعمل", platform: "المنصة", contact: "تواصل معنا", trust: "الأمن والثقة", privacy: "سياسة الخصوصية", cookies: "سياسة ملفات تعريف الارتباط", terms: "شروط الخدمة", tagline: "© 2026 EXOPILOT · ذكاء جودة الخدمة · معالجة صوتية محلية" },
};

export const translations = { en, hi, ar };
